import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Components
import Actual from "./Actual";
import Cash from "./Cash";
import Employee from "./Employee";
import ExecutiveOrders from "./ExecutiveOrders"; // <-- ADD THIS LINE
import Expenses from "./Expenses";
import Menu from "./Menu";
import Orders from "./Order";
import Payments from "./Payment";
import Performance from "./Performance";
import Purchase from "./Purchase";
import Revenue from "./Revenue";
import Sales from "./Sales";

import "./styles/Admin.css";

const STAFF_API = "https://salathai.co.tz/mrms/Staff.php";
const ORDERS_API = "https://salathai.co.tz/mrms/Orders.php";
const PAYMENTS_API = "https://salathai.co.tz/mrms/Payment.php";
const EXPENSES_API = "https://salathai.co.tz/mrms/Expenses.php";
const PURCHASES_API = "https://salathai.co.tz/mrms/Purchase.php";

const Admin = () => {
  const [activeNav, setActiveNav] = useState("Dashboard");
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [staffName, setStaffName] = useState("");
  const [role, setRole] = useState("");
  const [dashboardData, setDashboardData] = useState({
    ordersToday: 0,
    totalRevenueToday: 0,
    totalOrderWorthToday: 0,
    pendingPayments: 0,
    activeStaff: 0,
    recentExpenses: 0,
    recentPurchases: 0,
    reconciledDifference: 0,
    isBalanced: true,
    statusMessage: "Balanced",
    variancePercentage: 0
  });
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  const getToken = () => localStorage.getItem("token");

  // ================= AUTHENTICATION =================
  useEffect(() => {
    const token = getToken();
    const staff = JSON.parse(localStorage.getItem("staff") || "{}");

    if (!token || !staff || staff.role !== "Admin") {
      navigate("/");
      return;
    }

    setStaffName(staff.full_name);
    setRole(staff.role);
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // ================= ROUTE DETECTION =================
  useEffect(() => {
    const path = location.pathname;

    if (path.includes("/admin/orders")) setActiveNav("Order Management");
    else if (path.includes("/admin/purchases")) setActiveNav("Purchases");
    else if (path.includes("/admin/payments")) setActiveNav("Payments");
    else if (path.includes("/admin/expenses")) setActiveNav("Expenses");
    else if (path.includes("/admin/menu")) setActiveNav("Menu Management");
    else if (path.includes("/admin/employees")) setActiveNav("Employee Management");
    else if (path.includes("/admin/reports/sales")) setActiveNav("Sales Report");
    else if (path.includes("/admin/reports/performance")) setActiveNav("Waitress Report");
    else if (path.includes("/admin/reports/cash")) setActiveNav("Cash Balance");
    else if (path.includes("/admin/reports/revenue")) setActiveNav("Revenue Report");
    else if (path.includes("/admin/actual-balance")) setActiveNav("Actual Balance");
    else if (path.includes("/admin/special-order")) setActiveNav("Special Order"); // <-- ADD THIS
    else setActiveNav("Dashboard");
  }, [location]);

  // ================= FETCH DASHBOARD DATA =================
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = getToken();
      if (!token) {
        toast.error("Authentication required");
        navigate("/");
        return;
      }

      console.log("===== FETCHING DASHBOARD DATA =====");

      // Get today's date in the correct format
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const todayStr = `${year}-${month}-${day}`;
      
      console.log("Today's date:", todayStr);

      // Fetch all data
      const [ordersRes, paymentsRes, expensesRes, purchaseRes, staffRes] = await Promise.all([
        fetch(`${ORDERS_API}?admin=true&token=${token}`),
        fetch(`${PAYMENTS_API}?token=${token}`),
        fetch(`${EXPENSES_API}?token=${token}`),
        fetch(`${PURCHASES_API}?token=${token}`),
        fetch(`${STAFF_API}?token=${token}`)
      ]);

      // Parse data
      let orders = [];
      let payments = [];
      let expenses = [];
      let purchase = [];
      let staff = [];

      try {
        if (ordersRes.ok) {
          const rawOrders = await ordersRes.json();
          orders = Array.isArray(rawOrders) ? rawOrders : [];
          console.log("Total orders loaded:", orders.length);
        }
      } catch (e) {
        console.error("Orders parse error:", e);
      }

      try {
        if (paymentsRes.ok) {
          const rawPayments = await paymentsRes.json();
          payments = Array.isArray(rawPayments) ? rawPayments : [];
          console.log("Total payments loaded:", payments.length);
        }
      } catch (e) {
        console.error("Payments parse error:", e);
      }

      try {
        if (expensesRes.ok) {
          const rawExpenses = await expensesRes.json();
          expenses = Array.isArray(rawExpenses) ? rawExpenses : [];
          console.log("Total expenses loaded:", expenses.length);
        }
      } catch (e) {
        console.error("Expenses parse error:", e);
      }

      try {
        if (purchaseRes.ok) {
          const rawPurchase = await purchaseRes.json();
          purchase = Array.isArray(rawPurchase) ? rawPurchase : [];
          console.log("Total purchases loaded:", purchase.length);
        }
      } catch (e) {
        console.error("Purchases parse error:", e);
      }

      try {
        if (staffRes.ok) {
          const rawStaff = await staffRes.json();
          staff = Array.isArray(rawStaff) ? rawStaff : [];
          console.log("Total staff loaded:", staff.length);
        }
      } catch (e) {
        console.error("Staff parse error:", e);
      }

      // ===== FILTER TODAY'S DATA =====
      // Get only orders from today
      const todayOrders = orders.filter(order => {
        if (!order.created_at) return false;
        const orderDate = order.created_at.split(' ')[0] || order.created_at.split('T')[0];
        return orderDate === todayStr;
      });
      
      console.log("Today's orders:", todayOrders.length);

      // Get today's PAID payments
      const todayPaidPayments = payments.filter(p => {
        if (!p.created_at) return false;
        const paymentDate = p.created_at.split(' ')[0] || p.created_at.split('T')[0];
        const isPaid = p.status === 'PAID' || p.status === 'APPROVED';
        return isPaid && paymentDate === todayStr;
      });

      console.log("Today's paid payments:", todayPaidPayments.length);

      // ===== CALCULATE TOTALS =====
      const totalRevenueToday = todayPaidPayments.reduce((sum, p) => {
        return sum + (parseFloat(p.total_amount) || parseFloat(p.amount) || 0);
      }, 0);

      const totalOrderWorthToday = todayOrders.reduce((sum, order) => {
        return sum + (parseFloat(order.total_amount) || 0);
      }, 0);

      // Pending payments (all dates)
      const pendingPayments = payments.filter(p => p.status === 'PENDING').length;

      // Active staff
      const activeStaff = staff.filter(s => s.active === 'yes' || s.active === 1 || s.active === true).length;

      // Today's expenses
      const todayExpenses = expenses.filter(e => {
        if (!e.created_at) return false;
        const expenseDate = e.created_at.split(' ')[0] || e.created_at.split('T')[0];
        return expenseDate === todayStr;
      });
      const recentExpenses = todayExpenses.reduce((sum, e) => {
        return sum + (parseFloat(e.amount) || 0);
      }, 0);

      // Today's purchases
      const todayPurchases = purchase.filter(p => {
        const dateField = p.created_at || p.purchase_date;
        if (!dateField) return false;
        const purchaseDate = dateField.split(' ')[0] || dateField.split('T')[0];
        return purchaseDate === todayStr;
      });
      const recentPurchases = todayPurchases.reduce((sum, p) => {
        return sum + (parseFloat(p.amount) || parseFloat(p.total_price) || 0);
      }, 0);

      // ===== RECONCILIATION =====
      // Compare today's revenue vs today's orders
      const reconciledDifference = totalRevenueToday - totalOrderWorthToday;
      let variancePercentage = 0;
      if (totalOrderWorthToday > 0) {
        variancePercentage = (reconciledDifference / totalOrderWorthToday) * 100;
      }
      
      let statusMessage = "";
      let isBalanced = false;
      
      if (Math.abs(reconciledDifference) < 1) {
        statusMessage = "Balanced";
        isBalanced = true;
      } else if (reconciledDifference > 0) {
        statusMessage = `Over-collection (${variancePercentage.toFixed(1)}% above)`;
        isBalanced = false;
      } else {
        statusMessage = `Under-collection (${Math.abs(variancePercentage).toFixed(1)}% below)`;
        isBalanced = false;
      }

      console.log("Final dashboard data:", {
        ordersToday: todayOrders.length,
        totalRevenueToday,
        totalOrderWorthToday,
        pendingPayments,
        activeStaff,
        recentExpenses,
        recentPurchases,
        reconciledDifference
      });

      // ===== UPDATE STATE =====
      setDashboardData({
        ordersToday: todayOrders.length,
        totalRevenueToday,
        totalOrderWorthToday,
        pendingPayments,
        activeStaff,
        recentExpenses,
        recentPurchases,
        reconciledDifference,
        isBalanced,
        statusMessage,
        variancePercentage
      });

      setLoading(false);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
      setLoading(false);
    }
  };

  // ================= NAVIGATION FUNCTIONS =================
  const toggleMobileMenu = () => setShowMobileMenu(!showMobileMenu);
  
  const toggleSubMenu = (menu) => {
    setExpandedMenus(prev => ({ ...prev, [menu]: !prev[menu] }));
  };

  const handleLogout = () => {
    toast.info("Logging out...");
    setTimeout(() => {
      localStorage.removeItem("token");
      localStorage.removeItem("staff");
      navigate("/");
    }, 1000);
  };

  const formatCurrency = (amount) => {
    return `TZS ${Number(amount || 0).toLocaleString("en-US", { minimumFractionDigits: 0 })}`;
  };
  
  const formatDifference = (amount) => {
    const formatted = formatCurrency(Math.abs(amount));
    if (amount > 0) return `+${formatted}`;
    if (amount < 0) return `-${formatted}`;
    return formatted;
  };

  // Navigation items - ADD "Special Order" HERE
  const navItems = [
    { id: "Dashboard", label: "Dashboard", path: "/admin" },
    { id: "Order Management", label: "Orders", path: "/admin/orders" },
    { id: "Payments", label: "Payments", path: "/admin/payments" },
    { id: "Expenses", label: "Expenses", path: "/admin/expenses" },
    { id: "Purchases", label: "Purchases", path: "/admin/purchases" },
    { id: "Menu Management", label: "Menu", path: "/admin/menu" },
    { id: "Employee Management", label: "Employees", path: "/admin/employees" },
    { id: "Actual Balance", label: "Actual Balance", path: "/admin/actual-balance" },
    { id: "Special Order", label: "Special Order", path: "/admin/special-order" }, // <-- ADD THIS
    { 
      id: "Reports", 
      label: "Reports", 
      isSubmenu: true,
      children: [
        { id: "Sales Report", label: "Sales", path: "/admin/reports/sales" },
        { id: "Waitress Report", label: "Performance", path: "/admin/reports/performance" },
        { id: "Cash Balance", label: "Cash Balance", path: "/admin/reports/cash" },
        { id: "Revenue Report", label: "Revenue", path: "/admin/reports/revenue" }
      ]
    }
  ];

  return (
    <div className="admin-container">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* SIDEBAR */}
      <div className={`admin-sidebar ${showMobileMenu ? "mobile-active" : ""}`}>
        <div className="admin-sidebar-header">
          <div className="admin-brand">
            <div className="admin-brand-text">
              <h2>SALATHAI</h2>
              <span>Administration</span>
            </div>
          </div>
          <button className="admin-close-mobile" onClick={toggleMobileMenu}>
            ×
          </button>
        </div>

        <nav className="admin-nav">
          <ul className="admin-nav-list">
            {navItems.map((item) => {
              if (item.isSubmenu) {
                const isActive = activeNav.includes(item.id) || 
                  item.children.some(child => activeNav === child.id);
                const isExpanded = expandedMenus[item.id];
                
                return (
                  <li key={item.id} className="admin-nav-item">
                    <div
                      className={`admin-submenu-trigger ${isActive ? "active" : ""}`}
                      onClick={() => toggleSubMenu(item.id)}
                    >
                      <span>{item.label}</span>
                      <span className={`admin-submenu-arrow ${isExpanded ? "rotated" : ""}`}>▾</span>
                    </div>
                    {isExpanded && (
                      <ul className="admin-submenu-list">
                        {item.children.map((child) => (
                          <li
                            key={child.id}
                            className={activeNav === child.id ? "active" : ""}
                            onClick={() => { 
                              setActiveNav(child.id); 
                              navigate(child.path); 
                              setShowMobileMenu(false); 
                            }}
                          >
                            {child.label}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              }
              
              return (
                <li
                  key={item.id}
                  className={`admin-nav-item ${activeNav === item.id ? "active" : ""}`}
                  onClick={() => { 
                    setActiveNav(item.id); 
                    navigate(item.path); 
                    setShowMobileMenu(false); 
                  }}
                >
                  <span>{item.label}</span>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>

      {/* MAIN CONTENT */}
      <div className="admin-main">
        {/* TOP HEADER */}
        <header className="admin-header">
          <button className="admin-menu-toggle" onClick={toggleMobileMenu}>
            ☰
          </button>
          
          <div className="admin-header-title">
            <span className="admin-title-badge">SIS</span>
            <h1>SALATHAI INFORMATION SYSTEM</h1>
          </div>
          
          <div className="admin-header-user" onClick={() => setShowUserDropdown(!showUserDropdown)}>
            <div className="admin-user-avatar">
              <span>{staffName ? staffName.charAt(0).toUpperCase() : "A"}</span>
            </div>
            <div className="admin-user-info">
              <span className="admin-username">{staffName}</span>
              <span className="admin-user-role">{role}</span>
            </div>
            <span className={`admin-dropdown-arrow ${showUserDropdown ? "rotated" : ""}`}>▾</span>
            
            {showUserDropdown && (
              <div className="admin-user-dropdown">
                <div className="admin-dropdown-header">
                  <strong>{staffName}</strong>
                  <span>{role}</span>
                </div>
                <div className="admin-dropdown-divider"></div>
                <button onClick={handleLogout}>Logout</button>
              </div>
            )}
          </div>
        </header>

        {/* CONTENT AREA */}
        <div className="admin-content">
          {/* DASHBOARD */}
          {activeNav === "Dashboard" && (
            <div className="admin-dashboard">
              {loading ? (
                <div className="admin-loading">
                  <div className="admin-spinner"></div>
                  <p>Loading dashboard data...</p>
                </div>
              ) : (
                <>
                  {/* Welcome Section */}
                  <div className="admin-welcome">
                    <div className="admin-welcome-content">
                      <h1>Welcome, <span className="admin-welcome-name">{staffName}</span></h1>
                      <p>to SALATHAI INFORMATION SYSTEM (SIS)</p>
                    </div>
                    <div className="admin-welcome-date">
                      {new Date().toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="admin-stats">
                    <div className="admin-stat stat-primary">
                      <div className="stat-number">{dashboardData.ordersToday}</div>
                      <div className="stat-label">Today's Orders</div>
                    </div>

                    <div className="admin-stat stat-success">
                      <div className="stat-number">{formatCurrency(dashboardData.totalRevenueToday)}</div>
                      <div className="stat-label">Today's Revenue</div>
                    </div>

                    <div className="admin-stat stat-warning">
                      <div className="stat-number">{dashboardData.pendingPayments}</div>
                      <div className="stat-label">Pending Payments</div>
                    </div>

                    <div className="admin-stat stat-info">
                      <div className="stat-number">{dashboardData.activeStaff}</div>
                      <div className="stat-label">Active Staff</div>
                    </div>
                  </div>

                  {/* Today's Order Worth - NEW CARD */}
                  <div className="admin-stats" style={{ marginTop: '0px' }}>
                    <div className="admin-stat stat-purple">
                      <div className="stat-number">{formatCurrency(dashboardData.totalOrderWorthToday)}</div>
                      <div className="stat-label">Today's Order Worth</div>
                    </div>
                  </div>

                  {/* Reconciliation Section */}
                  <div className={`admin-reconciliation ${dashboardData.reconciledDifference === 0 ? 'balanced' : dashboardData.reconciledDifference > 0 ? 'profit' : 'loss'}`}>
                    <div className="reconciliation-header">
                      <div className="reconciliation-status">
                        <span className="reconciliation-icon">
                          {dashboardData.reconciledDifference === 0 ? '✓' : dashboardData.reconciledDifference > 0 ? '↑' : '↓'}
                        </span>
                        <div>
                          <h3>Reconciliation Status</h3>
                          <span className={`reconciliation-badge ${dashboardData.reconciledDifference === 0 ? 'balanced' : dashboardData.reconciledDifference > 0 ? 'profit' : 'loss'}`}>
                            {dashboardData.statusMessage}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="reconciliation-body">
                      <div className="reconciliation-main">
                        <span className="reconciliation-label">Difference</span>
                        <span className={`reconciliation-value ${dashboardData.reconciledDifference === 0 ? 'zero' : dashboardData.reconciledDifference > 0 ? 'positive' : 'negative'}`}>
                          {formatDifference(dashboardData.reconciledDifference)}
                        </span>
                      </div>
                      
                      <div className="reconciliation-details">
                        <div className="detail-row">
                          <span>Revenue (Payments)</span>
                          <span className="detail-revenue">{formatCurrency(dashboardData.totalRevenueToday)}</span>
                        </div>
                        <div className="detail-row">
                          <span>Order Worth</span>
                          <span className="detail-order">{formatCurrency(dashboardData.totalOrderWorthToday)}</span>
                        </div>
                        <div className="detail-row variance">
                          <span>Variance</span>
                          <span className={`detail-variance ${dashboardData.reconciledDifference > 0 ? 'positive' : dashboardData.reconciledDifference < 0 ? 'negative' : 'zero'}`}>
                            {dashboardData.variancePercentage > 0 ? `+${dashboardData.variancePercentage.toFixed(1)}%` : 
                            dashboardData.variancePercentage < 0 ? `${dashboardData.variancePercentage.toFixed(1)}%` : '0%'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="admin-quick-stats">
                    <div className="quick-stat">
                      <span className="quick-stat-value">{formatCurrency(dashboardData.recentExpenses)}</span>
                      <span className="quick-stat-label">Today's Expenses</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* RENDER COMPONENTS */}
          {activeNav === "Order Management" && <Orders />}
          {activeNav === "Purchases" && <Purchase />}
          {activeNav === "Payments" && <Payments />}
          {activeNav === "Expenses" && <Expenses />}
          {activeNav === "Menu Management" && <Menu />}
          {activeNav === "Employee Management" && <Employee />}
          {activeNav === "Sales Report" && <Sales />}
          {activeNav === "Waitress Report" && <Performance />}
          {activeNav === "Cash Balance" && <Cash />}
          {activeNav === "Revenue Report" && <Revenue />}
          {activeNav === "Actual Balance" && <Actual />}
          {activeNav === "Special Order" && <ExecutiveOrders />} {/* <-- ADD THIS */}
        </div>
      </div>
    </div>
  );
};

export default Admin;